/**
 * Générateur — Répéter à la main une séquence d'instructions pour accomplir une tâche imposée
 * Attendu ID : 689 | Niveau : 6ème | Domaine : Algorithmique (initiation à la programmation)
 *
 * Types générés (2 catégories, 8 formats) :
 *   - derouler_boucle_qcm    : QCM 3 choix — quel chemin obtient-on en déroulant la boucle ?
 *   - arrivee_boucle_saisie  : saisie — case d'arrivée après déroulement complet (ex. « B5 »)
 *   - nb_instructions_saisie : saisie — nombre total d'instructions exécutées
 *   - boucle_equivalente_qcm : QCM 4 choix — quelle boucle équivaut au programme déroulé ?
 *   - derouler_vf            : V/F 4 lignes — affirmations sur le déroulement de la boucle
 *   - repeter_nombre_qcm / _saisie / _texte_trou : répéter n fois « multiplie
 *     par f » sur un nombre de départ (carte transplantée du gen 638, revue
 *     2026-08-27, ré-habillée en instructions : rien à identifier, on exécute).
 *
 * Langage commun aux gens 687-690 : grille 5×5, flèches ↑ ↓ ← →, robot sur la
 * case « D », coordonnées lignes A-E / colonnes 1-5. Le programme est ici un
 * bloc « répéter n fois [motif] » (motif de 2-3 flèches, n de 2 à 4) que
 * l'élève DÉROULE À LA MAIN — le motif et le nombre de répétitions sont
 * toujours donnés, jamais à construire (frontière avec le 690), et jamais de
 * simple reconnaissance sans déroulement (687).
 *
 * Invariants par construction :
 *   • le programme déroulé tient toujours dans la grille (retentatives) ;
 *   • QCM chemins : leurres = boucle mutée (n ± 1, motif inversé, flèche
 *     retournée), chemins dessinables, distincts entre eux et de la bonne ;
 *   • boucle équivalente : les leurres déroulés sont vérifiés ≠ du programme
 *     déroulé affiché (jamais d'équivalence accidentelle) ;
 *   • V/F : clés sémantiques distinctes, quota variable.
 *
 * Sources d'inspiration (recherche 2026-08-11) : Sésamath ex. 2 et 7 (dérouler
 * une répétition), ex. 5 scripts C/D (position finale avec boucle), carnet
 * d'algorithmique débranchée série 4, eduscol (simplifier un codage).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 689;

const L_DER = 'Dérouler une boucle : retrouver le chemin (QCM)';
const L_ARR = 'Dérouler une boucle : la case d\'arrivée (saisie)';
const L_NBI = 'Compter les instructions exécutées (saisie)';
const L_EQU = 'Écrire un programme avec une boucle (QCM)';
const L_VF  = 'Dérouler une boucle (V/F)';

const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Moteur de déplacements (partagé avec les gens 687-688) ──

const TAILLE = 5;

const DIRS = {
    '↑': { dx: 0, dy: -1 },
    '↓': { dx: 0, dy: 1 },
    '←': { dx: -1, dy: 0 },
    '→': { dx: 1, dy: 0 },
};
const FLECHES = Object.keys(DIRS);
const OPPOSE = { '↑': '↓', '↓': '↑', '←': '→', '→': '←' };

const LIGNES_NOMS = ['A', 'B', 'C', 'D', 'E'];
const nomCase = (c) => `${LIGNES_NOMS[c.y]}${c.x + 1}`;

const executer = (depart, prog) => {
    const cells = [{ ...depart }];
    let { x, y } = depart;
    for (const f of prog) {
        x += DIRS[f].dx;
        y += DIRS[f].dy;
        if (x < 0 || y < 0 || x >= TAILLE || y >= TAILLE) return null;
        cells.push({ x, y });
    }
    return cells;
};

const cleChemin = (cells) => cells.map(c => `${c.x},${c.y}`).join('|');

const estAutoEvitant = (cells) =>
    cells !== null && new Set(cells.map(c => `${c.x},${c.y}`)).size === cells.length;

const derouler = (motif, n) => {
    const prog = [];
    for (let i = 0; i < n; i++) prog.push(...motif);
    return prog;
};

/** Motif de `lg` flèches sans paire opposée consécutive (y compris au raccord des tours). */
const tirerMotif = (lg) => {
    for (let essai = 0; essai < 100; essai++) {
        const motif = [];
        for (let i = 0; i < lg; i++) {
            const interdites = i > 0 ? [OPPOSE[motif[i - 1]]] : [];
            motif.push(randomChoice(FLECHES.filter(f => !interdites.includes(f))));
        }
        if (motif[0] !== OPPOSE[motif[lg - 1]]) return motif;
    }
    return ['→', '↑'].slice(0, lg);
};

/**
 * Tire une boucle (motif, n, départ) dont le déroulé tient dans la grille.
 * options : { dessinable } → déroulé auto-évitant (chemin tracé lisible).
 *
 * n est tiré D'ABORD, à plat, puis motif/départ sont retentés à n FIXÉ
 * (revue 2026-08-12) : retirer n à chaque essai laissait les petits n
 * rafler les tirages (n = 4 : 5 % des exercices au lieu de ~33 %).
 */
const tirerBoucle = (lgMotif, nMin, nMax, options = {}) => {
    const plage = [];
    for (let n = nMin; n <= nMax; n++) plage.push(n);
    for (const n of shuffle(plage)) {
        for (let essai = 0; essai < 300; essai++) {
            const motif = tirerMotif(lgMotif);
            const depart = { x: randInt(0, TAILLE - 1), y: randInt(0, TAILLE - 1) };
            const cells = executer(depart, derouler(motif, n));
            if (cells === null) continue;
            if (options.dessinable && !estAutoEvitant(cells)) continue;
            const arrivee = cells[cells.length - 1];
            if (arrivee.x === depart.x && arrivee.y === depart.y) continue;
            return { motif, n, depart, cells };
        }
    }
    // Repli déterministe : escalier depuis le coin bas-gauche.
    const motif = ['→', '↑'];
    const n = 2;
    const depart = { x: 0, y: TAILLE - 1 };
    return { motif, n, depart, cells: executer(depart, derouler(motif, n)) };
};

/**
 * Taille de motif et plage de n cohérentes (revue 2026-08-12) : un motif de
 * 3 flèches sans demi-tour ne peut pas être répété 3 fois dans une grille
 * 5×5 — ces tirages finissaient tous sur le repli déterministe.
 */
const tirerBoucleEquilibree = (options = {}) => {
    const lgMotif = randomChoice([2, 2, 3]);
    return lgMotif === 2
        ? tirerBoucle(2, 2, 4, options)
        : tirerBoucle(3, 2, 2, options);
};

// ─── Mutations de boucle (leurres vérifiés) ──────────────────

const variantesBoucle = (motif, n) => {
    const variantes = [];
    if (n > 2) variantes.push({ motif, n: n - 1 });
    variantes.push({ motif, n: n + 1 });
    const renverse = [...motif].reverse();
    if (renverse.join('') !== motif.join('')) variantes.push({ motif: renverse, n });
    for (let i = 0; i < motif.length; i++) {
        const copie = [...motif];
        copie[i] = OPPOSE[copie[i]];
        variantes.push({ motif: copie, n });
    }
    for (let i = 0; i < motif.length; i++) {
        const copie = [...motif];
        const perps = DIRS[motif[i]].dx === 0 ? ['←', '→'] : ['↑', '↓'];
        copie[i] = randomChoice(perps);
        variantes.push({ motif: copie, n });
    }
    return variantes;
};

// ─── Rendu SVG ───────────────────────────────────────────────

const CASE = 26;
const BLEU = '#1E3A8A';
const VERT = '#1D9E75';
const ORANGE = '#B45309';

const svgToElement = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } }],
});

const enteteSvg = (w, h, aria) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="${aria}">`;

const svgGrille = (ox, oy, cells, options = {}) => {
    const w = TAILLE * CASE;
    const parts = [];
    for (let i = 0; i <= TAILLE; i++) {
        parts.push(`<line x1="${ox + i * CASE}" y1="${oy}" x2="${ox + i * CASE}" y2="${oy + w}" stroke="#cbd5e1" stroke-width="1"/>`);
        parts.push(`<line x1="${ox}" y1="${oy + i * CASE}" x2="${ox + w}" y2="${oy + i * CASE}" stroke="#cbd5e1" stroke-width="1"/>`);
    }
    parts.push(`<rect x="${ox}" y="${oy}" width="${w}" height="${w}" fill="none" stroke="${BLEU}" stroke-width="1.4"/>`);
    if (options.axes) {
        for (let i = 0; i < TAILLE; i++) {
            parts.push(`<text x="${ox - 9}" y="${oy + i * CASE + CASE / 2 + 4}" font-size="11" font-weight="600" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${LIGNES_NOMS[i]}</text>`);
            parts.push(`<text x="${ox + i * CASE + CASE / 2}" y="${oy - 6}" font-size="11" font-weight="600" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${i + 1}</text>`);
        }
    }
    const centre = (c) => ({ cx: ox + c.x * CASE + CASE / 2, cy: oy + c.y * CASE + CASE / 2 });
    if (options.chemin) {
        const pts = options.chemin.map(c => { const p = centre(c); return `${p.cx},${p.cy}`; }).join(' ');
        const fin = centre(options.chemin[options.chemin.length - 1]);
        const avant = centre(options.chemin[options.chemin.length - 2]);
        const vx = Math.sign(fin.cx - avant.cx), vy = Math.sign(fin.cy - avant.cy);
        parts.push(`<polyline points="${pts}" fill="none" stroke="${VERT}" stroke-width="2.4"/>`);
        parts.push(`<path d="M${fin.cx + vx * 6},${fin.cy + vy * 6} L${fin.cx - vy * 4 - vx * 2},${fin.cy - vx * 4 - vy * 2} L${fin.cx + vy * 4 - vx * 2},${fin.cy + vx * 4 - vy * 2} Z" fill="${VERT}"/>`);
    }
    if (options.label) {
        parts.push(`<text x="${ox + w / 2}" y="${oy - 8}" font-size="12" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${options.label}</text>`);
    }
    const dep = centre(cells[0]);
    parts.push(`<circle cx="${dep.cx}" cy="${dep.cy}" r="8" fill="${BLEU}"/>`);
    parts.push(`<text x="${dep.cx}" y="${dep.cy + 3.5}" font-size="10" font-weight="700" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">D</text>`);
    return parts.join('');
};

/** Bloc « répéter n fois [motif] » façon blocs de programmation. */
const svgBoucle = (ox, oy, motif, n) => {
    const wMotif = motif.length * 30 - 8;
    const w = Math.max(wMotif + 20, 112);
    const blocs = motif.map((f, i) => {
        const x = ox + 10 + i * 30;
        return `<rect x="${x}" y="${oy + 24}" width="22" height="22" rx="4" fill="#EFF6FF" stroke="${BLEU}" stroke-width="1.2"/>` +
            `<text x="${x + 11}" y="${oy + 40}" font-size="14" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${f}</text>`;
    }).join('');
    return `<rect x="${ox}" y="${oy}" width="${w}" height="56" rx="8" fill="#FFF7ED" stroke="${ORANGE}" stroke-width="1.6"/>` +
        `<text x="${ox + 10}" y="${oy + 16}" font-size="12" font-weight="700" fill="${ORANGE}" font-family="system-ui,sans-serif">répéter ${n} fois</text>` +
        blocs;
};

const largeurBoucle = (motif) => Math.max(motif.length * 30 - 8 + 20, 112);

const boucleEnTexte = (motif, n) => `répéter ${n} fois [ ${motif.join(' ')} ]`;

// Signature d'anti-répétition (revue 2026-08-12, recette gen 687) : le texte
// varie peu, le contenu variable vit surtout dans le SVG.
const signatureDe = (payload) =>
    (payload.elements_additionnels?.elements?.[0]?.config?.imageUrl || '') + payload.texte;

// ─── Format 1 — Dérouler la boucle : le chemin (QCM) ─────────

let derniereSignatureDer = null;

const genDeroulerBoucleQcm = () => {
    let payload = construireDeroulerBoucleQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureDer; t++) {
        payload = construireDeroulerBoucleQcm();
    }
    derniereSignatureDer = signatureDe(payload);
    return payload;
};

const construireDeroulerBoucleQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        // Motifs de 2 flèches (n 2-4) ou de 3 flèches (n = 2) — revue
        // 2026-08-12 : le motif de 2 seul plafonnait le pool à ~1260.
        const boucle = tirerBoucleEquilibree({ dessinable: true });
        const cleBonne = cleChemin(boucle.cells);

        // Leurres : boucles mutées au chemin dessinable, distincts de la bonne.
        const vus = new Set([cleBonne]);
        const leurres = [];
        for (const v of shuffle(variantesBoucle(boucle.motif, boucle.n))) {
            if (leurres.length === 2) break;
            const cells = executer(boucle.depart, derouler(v.motif, v.n));
            if (!estAutoEvitant(cells)) continue;
            const cle = cleChemin(cells);
            if (vus.has(cle)) continue;
            vus.add(cle);
            leurres.push(cells);
        }
        if (leurres.length < 2) continue;

        const chemins = shuffle([
            { cells: boucle.cells, correct: true },
            { cells: leurres[0], correct: false },
            { cells: leurres[1], correct: false },
        ]);
        const lettres = ['A', 'B', 'C'];
        const largeurGrille = TAILLE * CASE;
        const wBoucle = largeurBoucle(boucle.motif);
        const wTotal = 3 * largeurGrille + 60;
        const svg = enteteSvg(wTotal, largeurGrille + 110,
            `Un bloc répéter ${boucle.n} fois contenant ${boucle.motif.length} flèches, puis trois grilles nommées chemin A, B et C avec chacune un trajet depuis la case départ`)
            + svgBoucle((wTotal - wBoucle) / 2, 8, boucle.motif, boucle.n)
            + chemins.map((c, i) => svgGrille(10 + i * (largeurGrille + 20), 92, c.cells, { chemin: c.cells, label: `Chemin ${lettres[i]}` })).join('')
            + '</svg>';

        const bonne = `Chemin ${lettres[chemins.findIndex(c => c.correct)]}`;
        const texte = `<p>Le robot part de la case marquée « D » et exécute le programme ci-dessus : le motif entre crochets est répété ${boucle.n} fois de suite. Quel chemin obtient-on ?</p>`;

        const payload = makeQcm(lettres.map(l => `Chemin ${l}`), bonne, texte, opts(L_DER));
        payload.elements_additionnels = svgToElement(svg);
        payload._meta.generatorKey = 'derouler_boucle_qcm';
        return payload;
    }
    return construireDeroulerBoucleQcm();
};

// ─── Format 2 — Dérouler la boucle : l'arrivée (saisie) ──────

let derniereSignatureArrB = null;

const genArriveeBoucleSaisie = () => {
    let payload = construireArriveeBoucleSaisie();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureArrB; t++) {
        payload = construireArriveeBoucleSaisie();
    }
    derniereSignatureArrB = signatureDe(payload);
    return payload;
};

const construireArriveeBoucleSaisie = () => {
    const boucle = tirerBoucleEquilibree();
    const arrivee = boucle.cells[boucle.cells.length - 1];

    const largeurGrille = TAILLE * CASE;
    const wBoucle = largeurBoucle(boucle.motif);
    const w = Math.max(largeurGrille + 40, wBoucle + 20);
    const svg = enteteSvg(w, largeurGrille + 100,
        `Un bloc répéter ${boucle.n} fois contenant des flèches, et une grille à coordonnées lignes A à E, colonnes 1 à 5, avec la case départ D`)
        + svgBoucle((w - wBoucle) / 2, 4, boucle.motif, boucle.n)
        + svgGrille((w - largeurGrille) / 2 + 8, 78, boucle.cells, { axes: true })
        + '</svg>';

    const texte = `<p>Le robot part de la case marquée « D » et exécute le programme ci-dessus : le motif est répété ${boucle.n} fois de suite. Sur quelle case termine-t-il ? Écris le nom de la case, comme « B3 » (ligne puis colonne).</p>`;

    const payload = makeSaisie(nomCase(arrivee), texte, {
        ...opts(L_ARR),
        strict: false,
    });
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'arrivee_boucle_saisie';
    return payload;
};

// ─── Format 3 — Compter les instructions (saisie) ────────────

let derniereSignatureNbi = null;

const genNbInstructionsSaisie = () => {
    let payload = construireNbInstructionsSaisie();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureNbi; t++) {
        payload = construireNbInstructionsSaisie();
    }
    derniereSignatureNbi = signatureDe(payload);
    return payload;
};

const construireNbInstructionsSaisie = () => {
    const boucle = tirerBoucleEquilibree();
    const total = boucle.motif.length * boucle.n;

    const wBoucle = largeurBoucle(boucle.motif);
    const svg = enteteSvg(wBoucle + 20, 72,
        `Un bloc répéter ${boucle.n} fois contenant ${boucle.motif.length} flèches`)
        + svgBoucle(10, 8, boucle.motif, boucle.n)
        + '</svg>';

    // Énoncé sans les deux nombres (revue 2026-08-12) : ils se lisent dans le
    // bloc — les donner dans la phrase revenait à donner la réponse en kit.
    const texte = `<p>Le robot exécute le programme ci-dessus. Combien d'instructions (flèches) exécute-t-il en tout ?</p>`;

    const payload = makeSaisie(String(total), texte, {
        ...opts(L_NBI),
        strict: false,
        suffixe: ' instructions',
    });
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'nb_instructions_saisie';
    return payload;
};

// ─── Format 4 — Boucle équivalente (QCM) ─────────────────────

let derniereSignatureEqu = null;

const genBoucleEquivalenteQcm = () => {
    let payload = construireBoucleEquivalenteQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureEqu; t++) {
        payload = construireBoucleEquivalenteQcm();
    }
    derniereSignatureEqu = signatureDe(payload);
    return payload;
};

const construireBoucleEquivalenteQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const motif = tirerMotif(2);
        const n = randInt(2, 4);
        const deroule = derouler(motif, n);

        const cleDeroule = deroule.join('');
        const vus = new Set([cleDeroule, boucleEnTexte(motif, n)]);
        const leurres = [];
        for (const v of shuffle(variantesBoucle(motif, n))) {
            if (leurres.length === 3) break;
            const texteV = boucleEnTexte(v.motif, v.n);
            if (vus.has(texteV) || derouler(v.motif, v.n).join('') === cleDeroule) continue;
            vus.add(texteV);
            leurres.push(texteV);
        }
        if (leurres.length < 3) continue;

        const affichage = deroule.map((f, i) => `${i + 1}) ${f}`).join('&nbsp;&nbsp; ');
        const texte = `<p>Voici le programme exécuté par le robot, lu de gauche à droite :</p>`
            + `<p><strong>${affichage}</strong></p>`
            + `<p>Quelle écriture avec une boucle « répéter » fait exécuter exactement le même programme, dans le même ordre ?</p>`;

        const bonne = boucleEnTexte(motif, n);
        const payload = makeQcm([bonne, ...leurres], bonne, texte, opts(L_EQU));
        payload._meta.generatorKey = 'boucle_equivalente_qcm';
        return payload;
    }
    return construireBoucleEquivalenteQcm();
};

// ─── Format 5 — Dérouler une boucle (V/F) ────────────────────

let derniereSignatureVF689 = null;

const genDeroulerVf = () => {
    let payload = construireDeroulerVf();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureVF689; t++) {
        payload = construireDeroulerVf();
    }
    derniereSignatureVF689 = signatureDe(payload);
    return payload;
};

const autreCase = (cell) => {
    const autres = [];
    for (let x = 0; x < TAILLE; x++) {
        for (let y = 0; y < TAILLE; y++) {
            if (x !== cell.x || y !== cell.y) autres.push({ x, y });
        }
    }
    return randomChoice(autres);
};

// Chaque builder retourne { l, v, cases (cases nommées) } ou null.
// Revue 2026-08-12 : « le motif est exécuté n fois » et « le motif contient
// m instructions » se lisaient sur le bloc sans aucun déroulement, et
// formaient avec le total un trio mutuellement déductible — remplacées par
// « passe par la case X ». Les 4 familles restantes exigent le déroulement.
const BUILDERS_VF_689 = [
    {
        k: 'arrivee',
        b: (boucle, vrai) => {
            const arrivee = boucle.cells[boucle.cells.length - 1];
            const c = vrai ? arrivee : autreCase(arrivee);
            return { l: `À la fin du programme, le robot est sur la case ${nomCase(c)}.`, v: vrai, cases: [nomCase(c)] };
        },
    },
    {
        k: 'fin_tour_k',
        b: (boucle, vrai) => {
            const k = randInt(1, boucle.n - 1);
            const reelle = boucle.cells[k * boucle.motif.length];
            const c = vrai ? reelle : autreCase(reelle);
            return { l: `À la fin du ${k === 1 ? '1er' : `${k}e`} tour de boucle, le robot est sur la case ${nomCase(c)}.`, v: vrai, cases: [nomCase(c)] };
        },
    },
    {
        k: 'passe_par',
        b: (boucle, vrai) => {
            const surChemin = new Set(boucle.cells.map(c => `${c.x},${c.y}`));
            const dep = boucle.cells[0];
            const arr = boucle.cells[boucle.cells.length - 1];
            const candidates = [];
            for (let x = 0; x < TAILLE; x++) {
                for (let y = 0; y < TAILLE; y++) {
                    const interne = surChemin.has(`${x},${y}`);
                    const estExtremite = (x === dep.x && y === dep.y) || (x === arr.x && y === arr.y);
                    if (vrai && interne && !estExtremite) candidates.push({ x, y });
                    if (!vrai && !interne) candidates.push({ x, y });
                }
            }
            if (!candidates.length) return null;
            const c = randomChoice(candidates);
            return { l: `Au cours de son trajet, le robot passe par la case ${nomCase(c)}.`, v: vrai, cases: [nomCase(c)] };
        },
    },
    {
        k: 'total_instr',
        b: (boucle, vrai) => {
            const reel = boucle.motif.length * boucle.n;
            const nb = vrai ? reel : reel + randomChoice([-2, -1, 1, 2]);
            if (nb < 1) return null;
            return { l: `En tout, le robot exécute ${nb} instructions.`, v: vrai, cases: [] };
        },
    },
];

const construireDeroulerVf = () => {
    const boucle = tirerBoucleEquilibree();

    const largeurGrille = TAILLE * CASE;
    const wBoucle = largeurBoucle(boucle.motif);
    const w = Math.max(largeurGrille + 40, wBoucle + 20);
    const svg = enteteSvg(w, largeurGrille + 100,
        `Un bloc répéter ${boucle.n} fois contenant des flèches, et une grille à coordonnées lignes A à E, colonnes 1 à 5, avec la case départ D`)
        + svgBoucle((w - wBoucle) / 2, 4, boucle.motif, boucle.n)
        + svgGrille((w - largeurGrille) / 2 + 8, 78, boucle.cells, { axes: true })
        + '</svg>';

    const texte = `<p>Le robot part de la case marquée « D » et exécute le programme ci-dessus : le motif est répété ${boucle.n} fois de suite. Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;

    for (let essai = 0; essai < 60; essai++) {
        const nbV = randInt(1, 3);
        const camps = shuffle([...Array(4)].map((_, i) => i < nbV));
        const builders = shuffle([...BUILDERS_VF_689]);
        const items = builders.map((bd, i) => bd.b(boucle, camps[i]));
        if (items.some(x => x === null)) continue;
        // Cases nommées deux à deux distinctes (revue 2026-08-12).
        const casesNommees = items.flatMap(x => x.cases);
        if (new Set(casesNommees).size !== casesNommees.length) continue;
        const lignes = items.map(x => x.l);
        if (new Set(lignes).size !== 4) continue;
        const bonnesReponses = items.map(x => (x.v ? 0 : 1));
        const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
            ...opts(L_VF),
            elements_additionnels: svgToElement(svg),
        });
        payload._meta.generatorKey = 'derouler_vf';
        return payload;
    }
    return construireDeroulerVf();
};

// ─── Répéter une instruction sur un nombre ───────────────────
// Carte « suite multiplicative » du gen 638 transplantée ici (revue
// 2026-08-27, choix prof) et ré-habillée en langage d'instructions :
// l'instruction (« multiplie par f ») et le nombre de répétitions sont
// DONNÉS, l'élève exécute la répétition à la main. Plus aucune structure
// à identifier, c'est la frontière avec le 638 (motifs évolutifs).

const L_RQC = 'Répéter une instruction sur un nombre (QCM)';
const L_RSA = 'Répéter une instruction sur un nombre (saisie)';
const L_REX = 'Exécuter pas à pas une instruction répétée';

/**
 * Tire (init, facteur, n) où n = nombre d'applications de « × facteur ».
 * Contraintes héritées du gen 638 (rang = n + 1) : init × facteur^n ≤ 10000,
 * init ≤ 20, échantillonnage uniforme parmi tous les triplets valides.
 */
const genRepeterData = (nMin = null, nMax = null) => {
    const repsByFacteur = {
        2: [5, 6, 7, 8, 9, 10, 11, 12],
        3: [4, 5, 6, 7],
        5: [3, 4, 5],
        10: [2, 3, 4],
    };
    const combos = [];
    for (const fStr of Object.keys(repsByFacteur)) {
        const facteur = Number(fStr);
        for (const n of repsByFacteur[facteur]) {
            if (nMin !== null && n < nMin) continue;
            if (nMax !== null && n > nMax) continue;
            const maxInit = Math.min(Math.floor(10000 / Math.pow(facteur, n)), 20);
            for (let init = 1; init <= maxInit; init++) combos.push({ init, facteur, n });
        }
    }
    const { init, facteur, n } = randomChoice(combos);
    return { init, facteur, n, resultat: init * Math.pow(facteur, n) };
};

const addUnique689 = (set, val, correct) => {
    if (val !== correct && val > 0 && Number.isInteger(val)) set.add(val);
};

// Espace fine insécable des milliers (revue 2026-08-27) : « 1 152 » plutôt
// que « 1152 ». AFFICHAGE uniquement (QCM) — jamais sur une réponse à taper.
const espMilliers689 = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

/** Énoncé commun : le programme en langage d'instructions. */
const texteProgramme = (init, facteur, n, question) =>
    `<p>Voici un programme de calcul :</p><p>• Pars du nombre <strong>${init}</strong>.<br>• Répète <strong>${n} fois</strong> l\'instruction : « multiplie par ${facteur} ».</p><p>${question}</p>`;

// Anti-répétition immédiate (recette standard).
let derniereSignatureRepQcm689 = null;

const genRepeterQcm = () => {
    let payload = construireRepeterQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureRepQcm689; t++) {
        payload = construireRepeterQcm();
    }
    derniereSignatureRepQcm689 = payload.texte;
    return payload;
};

const construireRepeterQcm = () => {
    const { init, facteur, n, resultat } = genRepeterData();
    const correct = resultat;

    // Anti « élimination des extrêmes » hérité du gen 638 (p = 1/3 calibré
    // revue 2026-08-02) : une part des tirages sert les trois distracteurs
    // SOUS le résultat pour que « cocher le max » ne rapporte rien.
    const dSet = new Set();
    if (Math.random() < 1 / 3) {
        addUnique689(dSet, init + n * facteur, correct);   // a ajouté au lieu de multiplier
        addUnique689(dSet, correct / facteur, correct);    // une application de moins
        addUnique689(dSet, init * n * facteur, correct);   // confusion init × n × facteur
        addUnique689(dSet, init * facteur, correct);       // une seule application
    } else {
        addUnique689(dSet, init * n * facteur, correct);   // confusion init × n × facteur
        addUnique689(dSet, init + n * facteur, correct);   // a ajouté au lieu de multiplier
        addUnique689(dSet, correct * facteur, correct);    // une application de trop
        addUnique689(dSet, init * facteur, correct);       // une seule application
    }
    let att = 0;
    while (dSet.size < 3 && att < 30) {
        addUnique689(dSet, randInt(Math.max(1, Math.floor(correct * 0.3)), Math.floor(correct * 1.5)), correct);
        att++;
    }
    const choixList = [espMilliers689(correct), ...[...dSet].slice(0, 3).map(espMilliers689)];

    const texte = texteProgramme(init, facteur, n, 'Quel nombre obtiens-tu à la fin du programme ?');
    const payload = makeQcm(choixList, espMilliers689(correct), texte, opts(L_RQC));
    // Calculatrice AUTORISÉE (hérité du gen 638, revue 2026-08-02) : jusqu'à
    // 12 multiplications successives sur des nombres à 4 chiffres.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    return payload;
};

// Anti-répétition immédiate (recette standard).
let derniereSignatureRepSai689 = null;

const genRepeterSaisie = () => {
    let payload = construireRepeterSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureRepSai689; t++) {
        payload = construireRepeterSaisie();
    }
    derniereSignatureRepSai689 = payload.texte;
    return payload;
};

const construireRepeterSaisie = () => {
    const { init, facteur, n, resultat } = genRepeterData();
    const texte = texteProgramme(init, facteur, n, 'Quel nombre obtiens-tu à la fin du programme ?');
    const payload = makeSaisie(String(resultat), texte, opts(L_RSA));
    // Calculatrice AUTORISÉE (hérité du gen 638) : mêmes enchaînements que le
    // QCM, et la saisie exige le nombre exact.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    return payload;
};

// Anti-répétition immédiate (recette standard).
let derniereSignatureRepTrou689 = null;

const genRepeterTexteTrou = () => {
    let payload = construireRepeterTexteTrou();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureRepTrou689; t++) {
        payload = construireRepeterTexteTrou();
    }
    derniereSignatureRepTrou689 = payload.contenu.phrase;
    return payload;
};

const construireRepeterTexteTrou = () => {
    // n ∈ [5, 8] (équivalent du rang 6-9 du gen 638) : l'échafaudage montre
    // les 4 premières applications, la dernière ligne demande le résultat
    // après n. Avec n ≥ 5, il n'est jamais déjà écrit trois lignes plus haut.
    const { init, facteur, n, resultat } = genRepeterData(5, 8);

    const t = [];
    for (let i = 0; i < 5; i++) t.push(init * Math.pow(facteur, i));

    const phrase = `Départ : ${t[0]}.\nAprès 1 application : ${t[0]} × ${facteur} = {{${t[1]}}}.\nAprès 2 applications : ${t[1]} × ${facteur} = {{${t[2]}}}.\nAprès 3 applications : ${t[2]} × ${facteur} = {{${t[3]}}}.\nAprès 4 applications : ${t[3]} × ${facteur} = {{${t[4]}}}.\nAprès les ${n} applications, on obtient {{${resultat}}}.`;

    const texte = texteProgramme(init, facteur, n, 'Complète l\'exécution du programme, étape par étape.');
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_REX), mode: 'saisie', strict: false });
    // Calculatrice AUTORISÉE (hérité du gen 638) : la dernière étape enchaîne
    // jusqu'à 4 multiplications non guidées.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    derouler_boucle_qcm:    genDeroulerBoucleQcm,
    arrivee_boucle_saisie:  genArriveeBoucleSaisie,
    nb_instructions_saisie: genNbInstructionsSaisie,
    boucle_equivalente_qcm: genBoucleEquivalenteQcm,
    derouler_vf:            genDeroulerVf,

    // Carte « répéter une instruction sur un nombre » (du gen 638, revue 2026-08-27).
    repeter_nombre_qcm:        genRepeterQcm,
    repeter_nombre_saisie:     genRepeterSaisie,
    repeter_nombre_texte_trou: genRepeterTexteTrou,
};

export const CATEGORIES = [
    {
        id: 'repeter_sequences',
        label: 'Répéter à la main une séquence d\'instructions',
        formatsList: [
            { key: 'derouler_boucle_qcm',    label: L_DER },
            { key: 'arrivee_boucle_saisie',  label: L_ARR },
            { key: 'nb_instructions_saisie', label: L_NBI },
            { key: 'boucle_equivalente_qcm', label: L_EQU },
            { key: 'derouler_vf',            label: L_VF },
        ],
    },
    // Carte transplantée du gen 638 (revue 2026-08-27), ré-habillée en
    // langage d'instructions.
    {
        id: 'repeter_instruction_nombre',
        label: 'Répéter une instruction sur un nombre',
        formatsList: [
            { key: 'repeter_nombre_qcm',        label: L_RQC },
            { key: 'repeter_nombre_saisie',     label: L_RSA },
            { key: 'repeter_nombre_texte_trou', label: L_REX },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    TAILLE,
    derouler,
    tirerMotif,
    tirerBoucle,
    variantesBoucle,
    executer,
    nomCase,
    BUILDERS_VF_689,
    genDeroulerBoucleQcm,
    genArriveeBoucleSaisie,
    genNbInstructionsSaisie,
    genBoucleEquivalenteQcm,
    genDeroulerVf,
    genRepeterData,
    genRepeterQcm,
    genRepeterSaisie,
    genRepeterTexteTrou,
};
